const WEEK = 7 * 86_400_000

type ShapeTree = {
  dueDate?: number
  milestones: Array<{ dueDate?: number; targets: Array<{ tasks: unknown[] }> }>
  habits?: Array<{ cadence: string; days?: number[]; minutes?: number }>
}

const HABIT_PER_WEEK: Record<string, number> = {
  daily: 7,
  weekday: 5,
  weekend: 2,
  everyOtherDay: 3.5,
  weekly: 1,
  monthly: 0.25,
}

// Sessions a week a habit adds; a quick check-in (a 5-minute log) isn't a session.
export function habitSessionsPerWeek(h: {
  cadence: string
  days?: number[]
  minutes?: number
}): number {
  if (h.minutes !== undefined && h.minutes < 15) return 0
  return h.cadence === 'days' ? (h.days?.length ?? 0) : (HABIT_PER_WEEK[h.cadence] ?? 0)
}

export type PlanShape = {
  weeks: number | null
  // Per milestone: its 1-based week span, or null when the plan has no dates.
  spans: Array<{ from: number; to: number } | null>
  // Sessions in a typical week: weekTasks plus the habits' sessions.
  perWeek: number | null
  // One-off tasks in a typical week (for "This week"); the habits run alongside.
  weekTasks: number
}

// Week ranges for a draft plan: from milestone due dates when the AI set them,
// else the goal's deadline split by each milestone's share of the tasks.
export function planShape(tree: ShapeTree, now = Date.now()): PlanShape {
  const weekOf = (t: number) => Math.max(1, Math.ceil((t - now) / WEEK))
  const counts = tree.milestones.map((m) => m.targets.reduce((n, tg) => n + tg.tasks.length, 0))
  const total = counts.reduce((a, b) => a + b, 0)
  const lastDue = tree.milestones[tree.milestones.length - 1]?.dueDate
  const end = tree.dueDate ?? lastDue
  const weeks = end ? weekOf(end) : null
  const taskRate = total ? total / (weeks ?? 12) : 0
  const habitRate = (tree.habits ?? []).reduce((n, h) => n + habitSessionsPerWeek(h), 0)
  const weekTasks = total ? Math.min(3, Math.max(1, Math.round(taskRate))) : 0
  const perWeek = weekTasks + Math.round(habitRate) || null

  let spans: PlanShape['spans'] = tree.milestones.map(() => null)
  if (tree.milestones.every((m) => m.dueDate)) {
    let from = 1
    spans = tree.milestones.map((m) => {
      const to = Math.max(from, weekOf(m.dueDate!))
      const span = { from, to }
      from = to + 1
      return span
    })
  } else if (weeks) {
    let from = 1
    let done = 0
    spans = counts.map((c, i) => {
      done += c
      const last = i === counts.length - 1
      const to = last
        ? Math.max(from, weeks)
        : Math.max(from, Math.round((done / (total || 1)) * weeks))
      const span = { from, to }
      from = to + 1
      return span
    })
  }
  return { weeks, spans, perWeek, weekTasks }
}
